import { useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '@astro/api'
import { FormProvider, useForm } from '@astro/forms'
import {
  DEFAULT_VALUES, STEPS, buildRegistrationFormData, toRegistrationFieldErrors,
  StepBasicInfo, StepProfile, StepEducation, StepProfessional,
  StepAvailability, StepKyc, StepBank, StepAgreement,
} from '@astro/astrologer-forms'
import { useToast } from '../../../context/ToastContext'
import InfoPanel from './InfoPanel'
import StepNav from './StepNav'

const STEP_PANELS = [
  StepBasicInfo,
  StepProfile,
  StepEducation,
  StepProfessional,
  StepAvailability,
  StepKyc,
  StepBank,
  StepAgreement,
]

export default function AstrologerRegisterSection() {
  const [step, setStep] = useState(1);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Details captured on the account-creation form (astrologer checkbox flow)
  // arrive via router state and pre-fill the Basic Info step.
  const { state } = useLocation();
  const [prefill] = useState(() => state?.prefill ?? null);

  const methods = useForm({
    defaultValues: { ...DEFAULT_VALUES, ...prefill },
    mode: 'onTouched',
  });
  const { trigger, handleSubmit, setError } = methods;

  const scrollToTop = () => {
    const el = sectionRef.current
    if (el) window.scrollTo({ top: el.offsetTop - 120, behavior: 'smooth' })
  };

  const goStep = async (target) => {
    if (target < 1 || target > STEPS.length) return
    if (target > step) {
      const valid = await trigger(STEPS[step - 1].fields, { shouldFocus: true })
      if (!valid) return
    }
    setStep(target)
    scrollToTop()
  };

  const registerMutation = useMutation({
    mutationFn: (payload) => api.astrologers.register(payload),
    onSuccess: () => {
      showToast('Application submitted! Our team will review your profile within 24 hours.')
      window.setTimeout(() => navigate('/'), 2200)
    },
    onError: (err) => {
      // Server-side rejections (e.g. an upload failing the image/size check)
      // land on their own inputs; jump back to the earliest step holding one.
      const fieldErrors = toRegistrationFieldErrors(err?.error?.details)
      fieldErrors.forEach(({ name, message }) => setError(name, { type: 'server', message }))
      if (fieldErrors.length) {
        setStep(Math.min(...fieldErrors.map((e) => e.step)))
        scrollToTop()
      }
      // Validation copy is written for users; anything else (network drop
      // mid-upload, 413, 5xx) gets the generic line, never raw error text.
      showToast(
        err?.error?.status === 422 && err.message
          ? err.message
          : 'Submission failed — please check your details and try again.'
      )
    }
  });

  // Multipart, so the profile photo and KYC documents upload with the application.
  const onSubmit = (data) => {
    registerMutation.mutate(buildRegistrationFormData(data));
  };

  const meta = STEPS[step - 1];
  const StepPanel = STEP_PANELS[step - 1];
  const isLastStep = step === STEPS.length;

  return (
    <section ref={sectionRef} id="registration" className="astro-form max-w-380 mx-auto px-4 sm:px-6 lg:px-10 py-14 -mt-8 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-[370px_minmax(0,1fr)] gap-8 items-start">
        <InfoPanel />

        <div className="bg-white rounded-[28px] shadow-[0_36px_90px_-32px_rgba(120,53,15,0.35)] border border-goldPrimary/15 overflow-hidden">
          <div className="h-1 bg-linear-to-r from-goldAccent via-goldPrimary to-goldAccent" aria-hidden="true" />
          <div className="grid grid-cols-1 md:grid-cols-[225px_1fr]">
            <StepNav currentStep={step} onStepClick={goStep} />

            <div className="p-6 sm:p-8 md:p-10">
              <div className="flex items-center justify-between mb-7">
                <div>
                  <h2 className="text-[22px] md:text-2xl font-royal text-astroDark">{meta.heading}</h2>
                  <p className="text-xs text-stone-400 mt-1">{meta.subheading}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-goldAccent bg-goldPrimary/10 px-3 py-1.5 rounded-full whitespace-nowrap">
                  Step {step} / {STEPS.length}
                </span>
              </div>

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <StepPanel />

                  <div className="flex items-center gap-3 pt-7 mt-3 border-t border-stone-100">
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={() => goStep(step - 1)}
                        className="w-1/3 border border-stone-300 text-stone-500 hover:text-goldAccent hover:border-goldAccent/40 font-bold text-xs tracking-[0.18em] uppercase py-3.5 rounded-xl transition-colors flex items-center justify-center gap-1.5"
                      >
                        <span>←</span> Back
                      </button>
                    )}
                    {isLastStep ? (
                      <button
                        type="submit"
                        disabled={registerMutation.isPending}
                        className="shine-btn flex-1 bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-xs tracking-[0.2em] uppercase py-3.5 rounded-xl shadow-lg shadow-goldPrimary/5 transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                      >
                        {registerMutation.isPending ? 'Submitting…' : (
                          <>Submit Application <span className="text-base leading-none">✓</span></>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => goStep(step + 1)}
                        className="shine-btn flex-1 bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-xs tracking-[0.2em] uppercase py-3.5 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                      >
                        Next <span className="text-base leading-none">→</span>
                      </button>
                    )}
                  </div>
                </form>
              </FormProvider>

              <p className="text-xs text-stone-400 text-center mt-6">
                Already registered?{' '}
                <Link to="/login" className="text-goldAccent font-semibold hover:underline">
                  Sign in instead
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
